import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  MonthCaption,
  type DayProps,
  type Modifiers,
  type MonthCaptionProps,
  type PropsBase,
} from 'react-day-picker';
import { Calendar, CalendarDayButton, proximaSelecao } from '@voll-intelligence/ui-kit/ui/calendar';
import { calendarFormatters, calendarLabels, calendarLocale } from './calendar_locale.js';
import { formatCalendarDay, parseDateOnly, startOfDay, toDateOnly } from '@voll-intelligence/dates';
import {
  earlierMonth,
  monthStart,
  monthsFrom,
  useCalendarMonths,
  type ColumnGrowth,
} from './travel_calendar_months.js';
import { useKeyedValue } from './use_keyed_value.js';
import { useStableCallback } from './use_stable_callback.js';
import { PopoverFrame, SheetFrame } from './travel_calendar_frames.js';
import './calendar_motion.css';

/** Um período em datas-só (`YYYY-MM-DD`), aberto enquanto falta o fim. É o
 *  vocabulário neutro do calendário: o hotel o traduz para check-in/check-out,
 *  o carro para retirada/devolução, o voo para ida/volta. Em `mode="single"`
 *  só `from` existe. */
export type DateRange = { from?: string; to?: string };

export type HeatTier = 1 | 2 | 3 | 4 | 5;

/** A camada de COR de um dia, por baixo do número (o mapa de calor de preço do
 *  aéreo): um degrau da rampa `heat-1..5` do DS, ou a espera pela leitura
 *  (`loading`, o shimmer). Nada aqui bloqueia o dia — escolher data nunca
 *  depende do preço (#2330); quem espera é só a cor. */
export type DayTone =
  | { readonly kind: 'heat'; readonly tier: HeatTier }
  | { readonly kind: 'loading' };

/** A parte do calendário que não depende de como se escolhe. */
export type TravelCalendarBaseProps = {
  readonly value: DateRange;
  readonly onChange: (value: DateRange) => void;
  readonly locale: string;
  readonly previousMonthLabel: string;
  readonly nextMonthLabel: string;
  /** Quantos meses lado a lado no popover. Dois é a saia dos popovers (29rem);
   *  UM é o que cabe no cartão da folha do celular. Na folha não vale. */
  readonly months?: 1 | 2;
  /** `popover` (default): `months` meses lado a lado, célula de 30px, rodapé
   *  no fluxo. `sheet`: o desenho da folha de tela cheia do celular (#2653,
   *  #3446, #5662) — o `DateSheetBody`: a linha dos dias da semana pregada no
   *  topo, a coluna infinita de meses no rolo, um por parada, sem setas, e o
   *  rodapé pregado embaixo. É a pele para dentro de uma `BottomSheet`: a raiz
   *  assume `flex-1` e mede pela folha. */
  readonly layout?: 'popover' | 'sheet';
  /** O piso do PESCÁVEL (`YYYY-MM-DD`), inclusive. Ausente ⇒ hoje. É a data do
   *  trecho anterior no multi do voo, ou a ida enquanto se escolhe a volta: um
   *  trecho não parte antes de o anterior chegar. Trava também a navegação — a
   *  grade não abre mês anterior ao do piso nem ao 1º em cena —, mas só move a
   *  vista quando ela acaba antes do piso (#4280). O anel de hoje segue em hoje. */
  readonly minDate?: string;
  /** O teto do PESCÁVEL (`YYYY-MM-DD`), inclusive. Ausente ⇒ sem teto. É a data de um gasto já
   *  feito (o KM): de hoje para trás. Não trava a navegação; os dias depois dele só não respondem. */
  readonly maxDate?: string;
  /** Which way time runs. `future` (default): the floor is today, and the sheet is born on it
   *  and grows down — the searches. `past`: the ceiling is today (or `maxDate`), with no floor
   *  but `minDate`; the sheet ends on the ceiling's month, is born scrolled to the value's month
   *  and grows UP, and the popover does not page past the ceiling — a day already lived (an
   *  expense, a KM leg). `free`: no floor or ceiling but `minDate`/`maxDate`; the sheet is born
   *  scrolled to the value's month (or today's) and grows both ways — a company's custom date. */
  readonly timeline?: 'future' | 'past' | 'free';
  /** O mês em cena (`YYYY-MM`), CONTROLADO. Ausente, o calendário guarda o
   *  seu: o popover nasce no mês do período salvo (uma estadia em dezembro não
   *  pode nascer em setembro com o período fora da vista), a folha no do piso. */
  readonly month?: string;
  readonly onMonthChange?: (month: string) => void;
  /** Os meses EM CENA (`YYYY-MM`), a cada mudança: o par do popover, ou a
   *  coluna da folha ao crescer. É por aqui que o aéreo pede o calor do que
   *  ainda não tem janela lida. */
  readonly onMonthsChange?: (months: readonly string[]) => void;
  /** A camada de cor de cada dia (ver `DayTone`); ausente, nenhum dia é pintado.
   *  Não pinta o passado nem a seleção — o ink da escolha vence a cor. */
  readonly dayTone?: (date: string) => DayTone | undefined;
  /** Há leitura em voo que vai mudar as cores — `aria-busy` na grade. */
  readonly busy?: boolean;
  /** Congelado: sem escolher nem navegar, a seleção segue desenhada. */
  readonly readOnly?: boolean;
  /** O que o nome acessível da ponta ganha ("15 de setembro — IDA"): `start` na
   *  primeira data (e na única, em `single`), `end` na segunda. */
  readonly selectionTags?: { readonly start: string; readonly end: string };
  /** O rodapé: no popover, no fluxo sob a grade; na folha, pregado embaixo,
   *  fora do rolo. O resumo e o Confirmar do hotel e do carro
   *  (`TravelCalendarConfirm`), a saia de estado do aéreo. */
  readonly footer?: ReactNode;
  /** `dropdown`: month and year menus in the caption, for a day years away (a birthdate). The
   *  menus span the floor (or 100 years back) to the ceiling (or 20 years ahead). Popover only:
   *  the sheet scrolls instead. */
  readonly caption?: 'label' | 'dropdown';
};

type Selection =
  | {
      readonly mode?: 'range';
      /** `1` (hotel): fim e início nunca caem no mesmo dia — ninguém reserva
       *  zero diárias. `0` (carro, voo): começar e terminar no mesmo dia é um
       *  período válido; o primeiro toque ainda só ABRE o período (ver
       *  `onSelect`). */
      readonly minNights: 0 | 1;
    }
  | {
      /** Uma data só (somente ida, um trecho do multi): tocar de novo mantém. */
      readonly mode: 'single';
    };

export type TravelCalendarProps = Selection & TravelCalendarBaseProps;

/** A cor dos dias, por contexto. `signature` é o que a identidade do valor
 *  segue: o valor só muda quando alguma cor em cena muda (ver `toneSignature`). */
const DayToneContext = createContext<
  { readonly signature: string; readonly tone: (date: string) => DayTone | undefined } | undefined
>(undefined);

function DayWithDateOnly(props: ComponentProps<typeof CalendarDayButton>): ReactElement {
  return <CalendarDayButton {...props} data-date={toDateOnly(props.day.date)} />;
}

/** `data-month-index` em cada legenda: o mês pela posição na grade, sem
 *  depender de classe do rdp. */
function MonthCaptionWithIndex(props: MonthCaptionProps): ReactElement {
  // O `MonthCaption` do rdp repassa ao `<div>` tudo que não é seu.
  return <MonthCaption {...props} data-month-index={props.displayIndex} />;
}

const HEAT_CLASS: Record<HeatTier, string> = {
  1: 'bg-heat-1',
  2: 'bg-heat-2',
  3: 'bg-heat-3',
  4: 'bg-heat-4',
  5: 'bg-heat-5',
};
/* A camada é recuada 2px nas laterais e não é o fundo da célula: sem o recuo,
   duas células de cores diferentes se encostam e a fronteira lê como uma
   terceira cor; e o recuo tem de morar na camada, porque a barra da seleção
   depende de as células se tocarem. Medido no `PainelDeDatas` do desenho. */
const TONE_LAYER = 'pointer-events-none absolute inset-x-[2px] inset-y-0 rounded-(--cell-radius)';

function DayToneLayer({
  tone,
  date,
}: {
  readonly tone: DayTone;
  readonly date: Date;
}): ReactElement {
  if (tone.kind === 'loading') {
    // A espera é da COR, não do dia: ocupa a mesma caixa que o tier vai ocupar.
    return (
      <span
        aria-hidden="true"
        data-tone="loading"
        className={`${TONE_LAYER} animate-pulse bg-muted`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      data-tone="heat"
      data-tier={tone.tier}
      /* `range-preview` é a marca que o primitivo põe na célula sob a prévia do
         hover: a faixa translúcida sobre o calor mudava de tom célula a célula,
         e a saída do designer foi esconder o dado embaixo (ver `ui/calendar`). */
      className={`${TONE_LAYER} heat-in [.range-preview_&]:invisible ${HEAT_CLASS[tone.tier]}`}
      /* A cascata sai da própria data (dia do mês + dia da semana): cresce na
         direção da leitura sem precisar do índice da linha. */
      style={{ animationDelay: `${String((date.getDate() + date.getDay()) * 14)}ms` }}
    />
  );
}

/** A célula: o `<td>` do rdp com a camada de cor ATRÁS do botão do dia. Lê a
 *  cor por contexto, não por closure — um `components.Day` criado por render
 *  remontaria a grade inteira a cada render (a nota do topo de `ui/calendar`). */
function TravelDay({ day, modifiers, children, ...tdProps }: DayProps): ReactElement {
  const tone = useContext(DayToneContext)?.tone(toDateOnly(day.date));
  const inSelection =
    modifiers.selected || modifiers.range_start || modifiers.range_middle || modifiers.range_end;
  // Pintar preço em dia passado é oferecer o que não está à venda; e o ink da
  // escolha já diz tudo sobre o dia escolhido.
  const paintable = tone !== undefined && !modifiers.hidden && !modifiers.past && !inSelection;
  return (
    <td {...tdProps}>
      {paintable && <DayToneLayer tone={tone} date={day.date} />}
      {children}
    </td>
  );
}

const components = {
  Day: TravelDay,
  DayButton: DayWithDateOnly,
  MonthCaption: MonthCaptionWithIndex,
};
/** A cor de cada dia dos meses em cena numa string: dois renders com a mesma
 *  assinatura pintam a mesma grade. É uma varredura de ~120 chamadas baratas,
 *  contra re-renderizar ~250 células. */
function toneSignature(
  dayTone: (date: string) => DayTone | undefined,
  firstMonth: string,
  count: number,
): string {
  const parts: string[] = [];
  for (const month of monthsFrom(firstMonth, count)) {
    const date = monthStart(month);
    while (toDateOnly(date).startsWith(month)) {
      const tone = dayTone(toDateOnly(date));
      parts.push(tone === undefined ? '' : tone.kind === 'heat' ? String(tone.tier) : 'l');
      date.setDate(date.getDate() + 1);
    }
  }
  return parts.join(',');
}

/** O nome acessível do dia: o do rdp mais a ponta que ele é ("— IDA"). */
function dayTag(
  modifiers: Modifiers,
  tags: NonNullable<TravelCalendarBaseProps['selectionTags']>,
): string | undefined {
  // Com `minNights: 0` o período pode fechar no dia em que abriu: o dia é as
  // DUAS pontas, e anunciar só a primeira esconde metade do que ele é.
  if (modifiers.range_start && modifiers.range_end) return `${tags.start} — ${tags.end}`;
  // Uma data só (`single`, ou o primeiro toque do período) é a ponta de início.
  if (
    modifiers.range_start ||
    (modifiers.selected && !modifiers.range_middle && !modifiers.range_end)
  ) {
    return tags.start;
  }
  if (modifiers.range_end) return tags.end;
  return undefined;
}

/** As peles da GRADE, resolvidas fora do render para o `TravelCalendar` não
 *  carregar uma condicional por classe.
 *  - `popover`: `months` meses lado a lado, célula de 30px.
 *  - `card`: um mês só no popover — é o cartão `Quando` da folha da conversa
 *    estreita. A grade de 210px sobrava encostada à esquerda de um cartão de
 *    ~330px: centra a grade e a célula mede pelo contêiner.
 *  - `sheet`: a folha de tela cheia (#2653) — os meses empilhados numa coluna
 *    infinita (padrão Airbnb/Google Flights mobile), um por parada do rolo
 *    (`snap-start`), célula de 1/7 da largura da folha por 40px de altura.
 *    Quem rola é o `DateSheetBody`, não o calendário. A linha do dia da semana
 *    sai de cada mês: a folha prega uma só (`SheetWeekdays`).
 *    Repõe a chave `months` inteira, como a nota do primitivo pede:
 *    direção de flex sozinha não faz o modo — e o modo empilhado troca junto as
 *    props de navegação (ver `hideNavigation`). */
type GridSkin = {
  readonly calendar: string;
  readonly months?: string;
  readonly month: string;
  readonly monthCaption?: string;
  readonly monthGrid: string;
};

function gridSkin(layout: 'popover' | 'sheet', months: 1 | 2): GridSkin {
  if (layout === 'sheet') {
    return {
      /* A geometria é a da folha do aéreo antes do calendário único (a que o
         time aprovou, #3446): a grade vai de borda a borda da folha, a célula
         mede 1/7 da largura e 40px de altura — no dedo a largura é ganho, e a
         barra da seleção e a camada de cor medem por `inset`, não por largura.
         Uma célula quadrada com teto (52px) parava em 396px encostada à
         esquerda, ou centrada com vão dos dois lados, em qualquer folha mais
         larga que um telefone (o iframe do host web, um tablet).
         `w-full`: a raiz do primitivo é `w-fit`. A célula do DS é `aspect-square`
         no `<td>` e no botão (os dois levam `rdp-day`); a altura fixa entra por
         cima dos dois. `--cell-size` fica só como o piso de largura do botão. */
      calendar:
        'iuk-calendar-motion w-full bg-transparent px-4 pt-0 pb-4 [--cell-size:2.5rem] [&_.rdp-day]:aspect-auto [&_.rdp-day]:h-10',
      /* Sem `relative`: ele só existia para ancorar a `nav` do primitivo, e
         a folha não tem `nav` — ancorá-la aqui era o defeito (#3197). */
      months: 'flex w-full flex-col gap-6',
      month: 'flex w-full snap-start flex-col gap-4',
      monthCaption: 'flex h-10 w-full items-center justify-center',
      monthGrid: 'w-full border-collapse',
    };
  }
  return {
    /* A raiz do primitivo é `w-fit` e a grade mede `--cell-size × 7`: no cartão a
       célula deriva da largura do contêiner, como a grade `1fr` do aéreo — piso
       de 30px (o popover largo) e teto de 44px (a folha da página), descontado o
       `p-1.5` dos dois lados. Centrar é aqui, não em `months` — e nos dois:
       o popover mede pelo conteúdo mais largo, que pode ser o rodapé (a saia
       do aéreo com a frase do agente passa dos 448px dos dois meses), e a grade
       `w-fit` sobrava encostada à esquerda com um vão só à direita. */
    calendar:
      months === 1
        ? 'iuk-calendar-motion mx-auto bg-transparent p-1.5 [--cell-size:clamp(1.875rem,calc((100cqw-0.75rem)/7),2.75rem)]'
        : 'iuk-calendar-motion mx-auto bg-transparent p-1.5 [--cell-size:1.875rem]',
    month: 'flex w-fit flex-col gap-4',
    monthGrid: 'w-[calc(var(--cell-size)*7)] border-collapse',
  };
}

function useDayLabels(
  locale: string,
  previousMonthLabel: string,
  nextMonthLabel: string,
  selectionTags: TravelCalendarBaseProps['selectionTags'],
) {
  // As pontas por valor: quem chama passa o par como literal a cada render.
  const startTag = selectionTags?.start;
  const endTag = selectionTags?.end;
  return useMemo(
    () => ({
      ...calendarLabels(locale, previousMonthLabel, nextMonthLabel),
      labelDayButton: (date: Date, modifiers: Modifiers) => {
        const base = formatCalendarDay(date, locale);
        const tag =
          startTag !== undefined && endTag !== undefined
            ? dayTag(modifiers, { start: startTag, end: endTag })
            : undefined;
        return tag ? `${base} — ${tag}` : base;
      },
    }),
    [locale, previousMonthLabel, nextMonthLabel, startTag, endTag],
  );
}

type GridProps = Pick<TravelCalendarBaseProps, 'locale' | 'readOnly' | 'value' | 'onChange'> & {
  readonly single: boolean;
  readonly minNights: 0 | 1;
  readonly skin: GridSkin;
  readonly labels: ReturnType<typeof useDayLabels>;
  readonly stacked: boolean;
  readonly monthsShown: number;
  readonly month: string;
  readonly onMonthChange: (next: Date) => void;
  readonly floor: string | undefined;
  readonly ceiling?: string;
  /** The last month the arrow opens (the `past` ceiling); absent, endless. */
  readonly lastMonth?: string;
  /** Month and year menus in the caption (see `TravelCalendarBaseProps.caption`). */
  readonly dropdown: boolean;
};

/** The menus' reach when the caption has them: rdp only offers up to the current year without
 *  `startMonth`/`endMonth`, and a visa's validity lives in the future. */
const DROPDOWN_YEARS_BACK = 100;
const DROPDOWN_YEARS_AHEAD = 20;

function yearsFromNow(years: number): string {
  return `${String(new Date().getFullYear() + years)}-${years < 0 ? '01' : '12'}`;
}

/** How far the arrows (and the caption's menus) go. */
function navBounds(props: GridProps): Pick<PropsBase, 'startMonth' | 'endMonth' | 'captionLayout'> {
  // A seta não abre mês antes do piso nem antes do 1º em cena. Só o piso
  // aqui faria o rdp re-ancorar o `month` controlado nele — o piso no 2º mês
  // do par empurrava a vista (#4280).
  const first =
    props.floor?.slice(0, 7) ?? (props.dropdown ? yearsFromNow(-DROPDOWN_YEARS_BACK) : undefined);
  const last = props.lastMonth ?? (props.dropdown ? yearsFromNow(DROPDOWN_YEARS_AHEAD) : undefined);
  return {
    ...(first === undefined ? {} : { startMonth: monthStart(earlierMonth(props.month, first)) }),
    ...(last === undefined ? {} : { endMonth: monthStart(last) }),
    ...(props.dropdown ? { captionLayout: 'dropdown' as const } : {}),
  };
}

/** A GRADE: o `Calendar` do DS com a pele, o piso e a escolha (período ou data
 *  única). Componente e não função porque os dois modos do rdp são elementos
 *  distintos, e assim o `TravelCalendar` fica com as molduras. */
function SelectionGrid(props: GridProps): ReactElement {
  const { locale, readOnly = false, value, onChange, single, minNights, skin, labels } = props;
  const dateFormatters = useMemo(() => calendarFormatters(locale), [locale]);
  const dayPickerLocale = useMemo(() => calendarLocale(locale), [locale]);
  const from = parseDateOnly(value.from) ?? undefined;
  const to = parseDateOnly(value.to) ?? undefined;
  // Read once, when the grid mounts: the clock is not read during render.
  const [today] = useState(() => startOfDay(new Date()));
  const floorDate = props.floor === undefined ? undefined : (parseDateOnly(props.floor) ?? today);
  const ceilingDate = parseDateOnly(props.ceiling);
  const outside = [
    ...(floorDate ? [{ before: floorDate }] : []),
    ...(ceilingDate ? [{ after: ceilingDate }] : []),
  ];
  const base = {
    lang: locale,
    locale: dayPickerLocale,
    className: skin.calendar,
    classNames: {
      ...(skin.months ? { months: skin.months } : {}),
      month: skin.month,
      ...(skin.monthCaption ? { month_caption: skin.monthCaption } : {}),
      month_grid: skin.monthGrid,
      week: 'mt-1 flex w-full',
      today: '[&>button]:font-semibold',
    },
    formatters: dateFormatters,
    labels,
    /* A folha desenha a coluna de meses de uma vez e ESCONDE a `nav` do
       primitivo: ela é `absolute top-0` no `months`, que na folha é o
       conteúdo do rolo — ao rolar para ver o segundo mês ela saía de cena
       (#3197). Na folha rolar é a navegação: sem setas (#5662). */
    numberOfMonths: props.monthsShown,
    hideNavigation: props.stacked,
    // O cabeçalho de colunas do rdp é `aria-hidden` (o nome do dia já diz o
    // dia da semana): na folha a linha visível é a pregada (`SheetWeekdays`).
    hideWeekdays: props.stacked,
    disableNavigation: readOnly,
    month: monthStart(props.month),
    onMonthChange: props.onMonthChange,
    // A seta não abre mês antes do piso nem antes do 1º em cena. Só o piso
    // aqui faria o rdp re-ancorar o `month` controlado nele — o piso no 2º mês
    // do par empurrava a vista (#4280).
    ...navBounds(props),
    // Congelado, nenhum dia responde; senão só o que está antes do piso ou depois do teto.
    disabled: readOnly ? true : outside,
    // `past` é o que a camada de cor lê: no congelado tudo é `disabled`, e as
    // cores lidas seguem desenhadas.
    modifiers: { past: floorDate ? { before: floorDate } : false },
    showOutsideDays: false,
    components,
  } satisfies Partial<PropsBase>;
  if (single) {
    return (
      <Calendar
        {...base}
        mode="single"
        required
        selected={from}
        onSelect={(date) => {
          onChange({ from: toDateOnly(date) });
        }}
      />
    );
  }
  return (
    <Calendar
      {...base}
      mode="range"
      selected={{ from, to }}
      // A prévia do período segue o cursor; no dedo ela acendia no toque e
      // ficava presa no último dia tocado (#5662).
      semPreviaDeRange={props.stacked}
      onSelect={(range, clicked) => {
        const next = proximaSelecao({ from, to }, range, clicked, { minNoites: minNights });
        // Com `minNights: 0` o rdp devolve o dia duplo no PRIMEIRO toque
        // (`{ from: dia, to: dia }`), e isso leria como "começa e termina no
        // mesmo dia" sem a pessoa ter dito nada disso. O primeiro toque só
        // abre o período; tocar o mesmo dia de novo é que o fecha nele.
        const firstTouch =
          !from && next?.from && next.to && next.from.toDateString() === next.to.toDateString();
        const resolved = firstTouch ? { from: next.from, to: undefined } : next;
        onChange({
          from: resolved?.from ? toDateOnly(resolved.from) : undefined,
          to: resolved?.to ? toDateOnly(resolved.to) : undefined,
        });
      }}
    />
  );
}

/** The pickable bounds of the timeline. `future`: the floor is `minDate` or today, and a
 *  `maxDate` only stops days. `past`: the ceiling is `maxDate` or today, which also ends the
 *  months, and there is no floor without `minDate`. The view is born on the saved value's month
 *  (a stay in December cannot be born in September with the stay out of view), else on the floor
 *  — or, in the `past`, on the ceiling. */
function timelineBounds(props: TravelCalendarProps, today: string) {
  const timeline = props.timeline ?? 'future';
  const past = timeline === 'past';
  const floor = props.minDate ?? (timeline === 'future' ? today : undefined);
  const ceiling = props.maxDate ?? (past ? today : undefined);
  // Without a value: the future opens on its floor, the past on its ceiling, `free` on today
  // (its floor is only a stop, like the vouchers' 2020).
  const start =
    props.value.from ?? (timeline === 'future' ? floor : past ? ceiling : today) ?? today;
  return {
    floor,
    ceiling,
    lastMonth: past ? ceiling?.slice(0, 7) : undefined,
    initialMonth: start.slice(0, 7),
    growth: GROWTH[timeline],
  };
}

const GROWTH = { future: 'down', past: 'up', free: 'both' } as const satisfies Record<
  NonNullable<TravelCalendarBaseProps['timeline']>,
  ColumnGrowth
>;

/**
 * O calendário dos seletores de data do produto — hotel, carro e voo — sobre o
 * `Calendar` do DS (react-day-picker): os meses a partir do piso, sem teto, em
 * três peles (ver `gridSkin`), com a escolha por período ou por data única, a
 * camada de cor por dia e o rodapé de quem chama. Nasceu como o calendário do
 * hotel, ficou genérico quando o carro trocou os dois instantes por um período
 * (#3446) e ganhou o que o aéreo precisava para deixar a grade própria; a
 * tradução para o vocabulário de cada vertical é de quem chama.
 */
export function TravelCalendar(props: TravelCalendarProps): ReactNode {
  const { months = 2, layout = 'popover', busy = false, footer } = props;
  const { locale, previousMonthLabel, nextMonthLabel } = props;
  const { dayTone, readOnly = false, onChange } = props;
  const [today] = useState(() => toDateOnly(new Date()));
  const { floor, ceiling, lastMonth, initialMonth, growth } = timelineBounds(props, today);
  const stacked = layout === 'sheet';
  const nav = useCalendarMonths({
    stacked,
    growth,
    floorMonth: floor?.slice(0, 7),
    ceilingMonth: lastMonth,
    initialMonth,
    monthProp: props.month,
    months,
    onMonthChange: props.onMonthChange,
    onMonthsChange: props.onMonthsChange,
  });
  const labels = useDayLabels(locale, previousMonthLabel, nextMonthLabel, props.selectionTags);
  const grid = useStableGrid({
    props,
    dayTone,
    readOnly,
    onChange,
    labels,
    stacked,
    monthsShown: stacked ? nav.shown : months,
    month: nav.month,
    setMonth: nav.setMonth,
    floor,
    ceiling,
    lastMonth,
  });
  if (stacked) {
    return (
      <SheetFrame scrollerRef={nav.scrollerRef} locale={locale} busy={busy} footer={footer}>
        {grid}
      </SheetFrame>
    );
  }
  return (
    <PopoverFrame card={months === 1} busy={busy} footer={footer}>
      {grid}
    </PopoverFrame>
  );
}

/**
 * A GRADE, re-renderizada só quando o que ela desenha muda: a escolha, os meses
 * em cena, o piso, os rótulos e a cor dos dias (`toneSignature`). Quem chama
 * re-renderiza por coisas que a grade não mostra — no aéreo, escolher a ida
 * relê o calor da volta, e o `busy` ligando e desligando e o rodapé mudando
 * re-renderizavam as ~250 células da folha a cada passo, cinco vezes num toque:
 * era a travada ao trocar de data no celular (#5693). Os callbacks entram
 * estáveis, para a identidade nova de cada render não contar como mudança.
 */
function useStableGrid({
  props,
  dayTone,
  readOnly,
  onChange: onChangeProp,
  labels,
  stacked,
  monthsShown,
  month,
  setMonth,
  floor,
  ceiling,
  lastMonth,
}: {
  readonly props: TravelCalendarProps;
  readonly dayTone: TravelCalendarProps['dayTone'];
  readonly readOnly: boolean;
  readonly onChange: TravelCalendarProps['onChange'];
  readonly labels: ReturnType<typeof useDayLabels>;
  readonly stacked: boolean;
  readonly monthsShown: number;
  readonly month: string;
  readonly setMonth: (next: Date) => void;
  readonly floor: string | undefined;
  readonly ceiling: string | undefined;
  readonly lastMonth: string | undefined;
}): ReactElement {
  const { locale, layout = 'popover', months = 2 } = props;
  const dropdown = props.caption === 'dropdown' && layout === 'popover';
  // A cor é lida DURANTE o render das células: o valor só troca com a assinatura,
  // que garante que o desenho de um `dayTone` mais novo é o mesmo.
  const signature = dayTone ? toneSignature(dayTone, month, monthsShown) : undefined;
  const toneValue = useKeyedValue(
    signature === undefined || !dayTone ? undefined : { signature, tone: dayTone },
    JSON.stringify(signature ?? null),
  );
  const onChange = useStableCallback(onChangeProp);
  const onMonthChange = useStableCallback(setMonth);
  const single = props.mode === 'single';
  const minNights = 'minNights' in props ? props.minNights : 0;
  const { from, to } = props.value;
  return useMemo(
    () => (
      <DayToneContext.Provider value={toneValue}>
        <SelectionGrid
          locale={locale}
          readOnly={readOnly}
          value={{ from, to }}
          onChange={onChange}
          single={single}
          minNights={minNights}
          skin={gridSkin(layout, months)}
          labels={labels}
          stacked={stacked}
          monthsShown={monthsShown}
          month={month}
          onMonthChange={onMonthChange}
          floor={floor}
          ceiling={ceiling}
          lastMonth={lastMonth}
          dropdown={dropdown}
        />
      </DayToneContext.Provider>
    ),
    [
      toneValue,
      locale,
      readOnly,
      from,
      to,
      onChange,
      single,
      minNights,
      layout,
      months,
      labels,
      stacked,
      monthsShown,
      month,
      onMonthChange,
      floor,
      ceiling,
      lastMonth,
      dropdown,
    ],
  );
}
